import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import "./ShowAll.scss";

export interface ShowAllConfig {
  readonly label?: string;
  readonly initialSelected?: boolean;
  readonly onToggle?: (selected: boolean) => void;
}

export interface ShowAllAPI {
  readonly getElement: () => HTMLElement;
  readonly setLabel: (label: string | null | undefined) => void;
  readonly setSelected: (selected: boolean) => void;
  readonly setVisible: (visible: boolean) => void;
  readonly destroy: () => void;
}

export const createShowAll = (config: ShowAllConfig = {}): ShowAllAPI => {
  const { label = "", initialSelected = false, onToggle } = config;

  let isDestroyed = false;
  let currentSelected = initialSelected;
  let currentLabel = label;

  const container = document.createElement("div");
  container.className = "show-all";

  const buttonWrapper = document.createElement("div");
  buttonWrapper.className = "show-all__button";

  const labelEl = document.createElement("span");
  labelEl.className = "show-all__label";
  labelEl.textContent = currentLabel;

  const iconButton: IconButtonAPI = createIconButton({
    icon: "page_all",
    behavior: "switch",
    size: 44,
    iconSize: 24,
    className: "show-all__icon-button",
    rounded: false,
    transparentBg: false,
    selected: currentSelected,
    onClick: (selected) => {
      if (typeof selected !== "boolean" || isDestroyed) return;
      currentSelected = selected;
      updateSelectedState();
      onToggle?.(selected);
    },
  });

  const updateSelectedState = () => {
    if (isDestroyed) return;
    iconButton.setSelected(currentSelected);
    container.classList.toggle("show-all--active", currentSelected);
  };

  const setLabel = (nextLabel: string | null | undefined) => {
    if (isDestroyed) return;
    currentLabel = nextLabel ?? "";
    labelEl.textContent = currentLabel;
    labelEl.style.display = currentLabel ? "" : "none";
  };

  const setSelected = (nextSelected: boolean) => {
    if (isDestroyed) return;
    if (currentSelected === nextSelected) return;
    currentSelected = nextSelected;
    updateSelectedState();
  };

  const setVisible = (visible: boolean) => {
    if (isDestroyed) return;
    container.style.display = visible ? "" : "none";
  };

  const destroy = () => {
    if (isDestroyed) return;
    isDestroyed = true;
    iconButton.destroy();
    container.remove();
  };

  buttonWrapper.appendChild(iconButton.getElement());
  container.appendChild(buttonWrapper);
  //container.appendChild(labelEl);

  updateSelectedState();
  //setLabel(label);

  return {
    getElement: () => container,
    setLabel,
    setSelected,
    setVisible,
    destroy,
  };
};

export const createShowAllInContainer = (
  container: HTMLElement,
  config: ShowAllConfig = {}
): ShowAllAPI => {
  const showAll = createShowAll(config);
  container.appendChild(showAll.getElement());
  return showAll;
};
